import React, { useEffect, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import LoginButton from './LoginButton';
import { config } from '../config';
import { loginRequest } from '../authConfig';
import { identifyUser, trackEvent } from '../utils/posthog';
import { getAuthToken } from '../utils/authToken';
import LegalConsentModal, { PendingDocument, ProfilingConsentStatus } from './LegalConsentModal';

interface AuthComponentProps {
    children: React.ReactNode;
}

interface UserInfo {
    id: string;
    email: string;
    displayName?: string;
    givenName?: string;
    surname?: string;
    hasPendingDeletion?: boolean;
    pendingDeletionInfo?: {
        deletionId: string;
        requestedAt: string;
        expiresAt: string;
    } | null;
}

const AuthComponent: React.FC<AuthComponentProps> = ({ children }) => {
    const isAuthenticated = useIsAuthenticated();
    const { instance, inProgress, accounts } = useMsal();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [authError, setAuthError] = useState<string | null>(null);
    const [showDeletionWarning, setShowDeletionWarning] = useState(false);
    const [isCancellingDeletion, setIsCancellingDeletion] = useState(false);
    const [showLegalConsentModal, setShowLegalConsentModal] = useState(false);
    const [pendingDocuments, setPendingDocuments] = useState<PendingDocument[]>([]);
    const [profilingConsent, setProfilingConsent] = useState<ProfilingConsentStatus | null>(null);

    // Check MSAL authentication and create user in background
    const checkAuth = async () => {
        if (!isAuthenticated || inProgress !== InteractionStatus.None) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setAuthError(null);

        try {
            // Get user info from MSAL token and show UI immediately
            const account = accounts[0];

            if (account) {
                // Extract user info from MSAL account (show UI immediately with basic info)
                setUserInfo({
                    id: account.localAccountId,
                    email: account.username,
                    displayName: account.name || account.username,
                    givenName: account.idTokenClaims?.given_name as string | undefined,
                    surname: account.idTokenClaims?.family_name as string | undefined
                });

                // Call getUserProfile to get full profile including deletion status
                instance.acquireTokenSilent({
                    ...loginRequest,
                    account: account
                }).then(response => {
                    // First, create/verify user in DB
                    fetch(`${config.apiUrl}/testAuth`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Auth-Token': `Bearer ${response.idToken}`
                        }
                    }).then(backendResponse => {
                        if (backendResponse.ok) {
                            console.log('[AuthComponent] User created/verified in database');
                        } else {
                            console.warn('[AuthComponent] Background user creation failed (will retry on first API call)');
                        }

                        // Then, get full user profile including deletion status
                        return fetch(`${config.apiUrl}/getUserProfile`, {
                            method: 'GET',
                            headers: {
                                'Content-Type': 'application/json',
                                'X-Auth-Token': `Bearer ${response.idToken}`
                            }
                        });
                    }).then(profileResponse => {
                        if (profileResponse && profileResponse.ok) {
                            return profileResponse.json();
                        }
                        return null;
                    }).then(profileData => {
                        if (profileData && profileData.user) {
                            // Update userInfo with full profile data
                            setUserInfo({
                                id: profileData.user.id,
                                email: profileData.user.email,
                                displayName: profileData.user.displayName || profileData.user.email,
                                givenName: profileData.user.givenName,
                                surname: profileData.user.surname,
                                hasPendingDeletion: profileData.user.hasPendingDeletion,
                                pendingDeletionInfo: profileData.user.pendingDeletionInfo
                            });

                            // Identify user in PostHog analytics
                            identifyUser(profileData.user.id, {
                                email: profileData.user.email,
                                displayName: profileData.user.displayName,
                                has_pending_deletion: profileData.user.hasPendingDeletion || false
                            });

                            // Track login event
                            trackEvent('user_logged_in', {
                                has_pending_deletion: profileData.user.hasPendingDeletion || false
                            });

                            // Show deletion warning if needed
                            if (profileData.user.hasPendingDeletion) {
                                setShowDeletionWarning(true);
                            }

                            // Check legal document status
                            fetch(`${config.apiUrl}/getLegalDocumentStatus`, {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json',
                                    'X-Auth-Token': `Bearer ${response.idToken}`
                                }
                            }).then(legalResponse => {
                                if (legalResponse.ok) {
                                    return legalResponse.json();
                                }
                                return null;
                            }).then(legalData => {
                                if (legalData) {
                                    if (legalData.profilingConsent) {
                                        setProfilingConsent(legalData.profilingConsent);
                                    }
                                    if (legalData.needsAcceptance) {
                                        setPendingDocuments(legalData.pendingDocuments);
                                        setShowLegalConsentModal(true);
                                    }
                                }
                            }).catch(err => {
                                console.warn('[AuthComponent] Failed to check legal document status:', err);
                            });
                        }
                    }).catch(err => {
                        console.warn('[AuthComponent] Background profile fetch failed:', err);
                    });
                }).catch(err => {
                    console.warn('[AuthComponent] Failed to get token for background auth:', err);
                });
            }
        } catch (error) {
            console.error('Auth error:', error);
            setAuthError('Failed to get user information');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        checkAuth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, inProgress]);

    if (!isAuthenticated) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '24px',
                    padding: '48px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                }}>
                    <div style={{
                        fontSize: '48px',
                        marginBottom: '16px'
                    }}>📄</div>
                    <h1 style={{
                        fontSize: '32px',
                        fontWeight: '700',
                        color: '#2d3748',
                        marginBottom: '12px',
                        letterSpacing: '-0.5px'
                    }}>SmartBloček</h1>
                    <p style={{
                        fontSize: '18px',
                        color: '#64748b',
                        marginBottom: '32px',
                        lineHeight: '1.5'
                    }}>Inteligentné spracovanie bločkov a rozdeľovanie nákladov</p>
                    <p style={{
                        fontSize: '16px',
                        color: '#64748b',
                        marginBottom: '24px'
                    }}>Pre pokračovanie sa musíte prihlásiť</p>
                    <LoginButton className="smart-login-btn" />
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh'
            }}>
                <p style={{ fontSize: '18px', color: '#667eea' }}>⏳ Prihlasujem vás...</p>
            </div>
        );
    }

    if (authError) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '20px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '24px',
                    padding: '48px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center',
                    maxWidth: '500px'
                }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
                    <h2 style={{
                        fontSize: '24px',
                        fontWeight: '600',
                        color: '#2d3748',
                        marginBottom: '16px'
                    }}>Chyba autentifikácie</h2>
                    <p style={{
                        fontSize: '16px',
                        color: '#64748b',
                        marginBottom: '24px',
                        lineHeight: '1.6'
                    }}>{authError}</p>
                    <LoginButton className="smart-login-btn" />
                </div>
            </div>
        );
    }

    if (!userInfo) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh'
            }}>
                <p>Načítavam používateľské údaje...</p>
            </div>
        );
    }

    // Get dynamic greeting based on time of day
    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 9) {
            return '☕ Dobré ráno';
        } else if (hour >= 9 && hour < 19) {
            return '👋 Ahoj';
        } else {
            return '🥂 Dobrý večer';
        }
    };

    // Handle cancelling deletion request
    const handleCancelDeletion = async () => {
        setIsCancellingDeletion(true);

        try {
            // Force token refresh to ensure we have a fresh token
            const token = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
                forceRefresh: true
            });

            const response = await fetch(`${config.apiUrl}/cancelDataDeletion`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': `Bearer ${token.idToken}`
                }
            });

            if (response.ok) {
                // Hide warning and update user info
                setShowDeletionWarning(false);
                if (userInfo) {
                    setUserInfo({
                        ...userInfo,
                        hasPendingDeletion: false,
                        pendingDeletionInfo: null
                    });
                }
            } else {
                const error = await response.json();
                console.error('Failed to cancel deletion:', error);

                // If token expired, try to re-login
                if (response.status === 401) {
                    alert('Tvoja relácia vypršala. Prosím prihlás sa znova.');
                    handleLogout();
                } else {
                    alert(`Nepodarilo sa zrušiť žiadosť: ${error.message || 'Neznáma chyba'}`);
                }
            }
        } catch (error) {
            console.error('Error cancelling deletion:', error);
            alert('Nepodarilo sa zrušiť žiadosť o vymazanie. Skúste to prosím neskôr.');
        } finally {
            setIsCancellingDeletion(false);
        }
    };

    // Handle logout - detect native auth vs redirect session
    const handleLogout = () => {
        if (localStorage.getItem('smartblocek_native_auth') === 'true') {
            sessionStorage.clear();
            window.location.href = '/auth/login';
        } else {
            instance.logoutRedirect({
                postLogoutRedirectUri: '/'
            });
        }
    };

    // Show legal consent modal if documents need acceptance
    if (showLegalConsentModal && pendingDocuments.length > 0) {
        return (
            <LegalConsentModal
                pendingDocuments={pendingDocuments}
                profilingConsent={profilingConsent}
                getAuthToken={() => getAuthToken(instance)}
                onAcceptAll={() => {
                    setShowLegalConsentModal(false);
                    setPendingDocuments([]);
                }}
            />
        );
    }

    // Show deletion warning if pending
    if (showDeletionWarning && userInfo?.hasPendingDeletion) {
        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: '20px',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
            }}>
                <div style={{
                    background: '#fff',
                    borderRadius: '20px',
                    maxWidth: '520px',
                    width: '100%',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                    overflow: 'hidden'
                }}>
                    {/* Header */}
                    <div style={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                        padding: '32px 28px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '56px', marginBottom: '12px' }}>⚠️</div>
                        <h1 style={{
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#fff',
                            margin: 0,
                            lineHeight: '1.3'
                        }}>
                            Čakáme na potvrdenie zmazania
                        </h1>
                    </div>

                    {/* Content */}
                    <div style={{ padding: '28px' }}>
                        <div style={{
                            background: '#fef2f2',
                            border: '2px solid #fecaca',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '24px'
                        }}>
                            <div style={{ fontSize: '15px', color: '#991b1b', lineHeight: '1.6', fontWeight: 500 }}>
                                <strong>Máš aktívnu žiadosť o zmazanie účtu.</strong>
                                <br /><br />
                                Ak chceš svoj účet a svoje dáta zmazať, potvrď to kliknutím na linku v e-maile, ktorý sme ti poslali.
                                <br /><br />
                                Ak si to rozmyslel(a) a chceš SmartBloček ďalej používať, klikni nižšie na Zrušiť žiadosť, zmazanie účtu zruším a môžeš SmartBloček ďalej používať.
                            </div>
                        </div>

                        <div style={{
                            background: '#f9fafb',
                            borderRadius: '8px',
                            padding: '14px',
                            marginBottom: '24px',
                            fontSize: '13px',
                            color: '#6b7280',
                            lineHeight: '1.6'
                        }}>
                            <strong>ℹ️ Čo sa stane:</strong><br />
                            • Ak potvrdíš vymazanie (cez e-mail) → účet a dáta budú natrvalo vymazané<br />
                            • Ak zrušíš žiadosť → môžeš pokračovať v používaní aplikácie normálne
                        </div>

                        {/* Action buttons */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                onClick={handleCancelDeletion}
                                disabled={isCancellingDeletion}
                                style={{
                                    background: '#667eea',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '16px 24px',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    cursor: isCancellingDeletion ? 'not-allowed' : 'pointer',
                                    opacity: isCancellingDeletion ? 0.6 : 1,
                                    transition: 'all 0.2s'
                                }}
                            >
                                {isCancellingDeletion ? 'Ruším žiadosť...' : 'Zrušiť zmazanie a pokračovať na SmartBloček'}
                            </button>

                            <button
                                onClick={handleLogout}
                                disabled={isCancellingDeletion}
                                style={{
                                    background: '#e5e7eb',
                                    color: '#374151',
                                    border: 'none',
                                    borderRadius: '12px',
                                    padding: '14px 24px',
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    cursor: isCancellingDeletion ? 'not-allowed' : 'pointer',
                                    opacity: isCancellingDeletion ? 0.5 : 1
                                }}
                            >
                                Odhlásiť sa
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // User is authenticated - show the app with header
    return (
        <div>
            {/* User info header - across all pages, no logout */}
            <div style={{
                background: '#f5f5f5',
                padding: '10px 20px',
                borderBottom: '1px solid #ddd',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <span>{getGreeting()}, {userInfo.displayName || userInfo.givenName || userInfo.email}</span>
            </div>

            {/* Main app content */}
            {children}
        </div>
    );
};

export default AuthComponent;